import { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import StandardModal from 'modals/StandardModal';
import Spinner from 'components/Spinner';
import Alert from 'view/components/Alert';
import { SubscriptionHelper } from 'view/helpers';
import { MODAL_TYPES } from 'view/ModalManager';
import {
	cancelSubscription,
	pauseSubscription,
	resumeSubscription,
	clearSubscriptionErrors,
	fetchAvailablePlans,
	upgradeSubscription,
	downgradeSubscription,
	checkDowngradeLimits,
	updateSmsAddon,
	NO_SMS_ADDON_CHANGE,
} from 'data/reducers/subscriptionReducer';
import { showToast } from 'data/reducers/toastReducer';
import { closeModal, openModal } from 'data/reducers/modalReducer';
import { useUpgradeIncentive } from 'hooks/useUpgradeIncentive';
import { SUBSCRIPTION_PLANS } from 'constants/subscriptions';

// The SMS add-on is sold as a monthly Stripe line item, so it can only ride on
// a monthly subscription; pairing it with a yearly plan produces a
// mixed-interval checkout Stripe refuses to create.
const SMS_ADDON_MONTHLY_PRICE = 19.99;
const SMS_ADDON_MONTHLY_ONLY_NOTE =
	'SMS Instant Alerts are billed monthly and cannot be combined with yearly billing. Switch to Monthly to include SMS.';

const SubscriptionActionModal = ({ options }) => {
	const dispatch = useDispatch();
	const { action, planId } = options || {};

	const {
		isPerformingAction,
		isCancelling,
		isPausing,
		isResuming,
		isCreatingPortalSession,
		cancelError,
		pauseError,
		resumeError,
		portalSessionError,
		availablePlans,
		currentSubscription,
		isUpgrading,
		upgradeError,
		isDowngrading,
		downgradeError,
	} = useSelector((state) => ({
		isPerformingAction: state.subscription.isPerformingAction,
		isCancelling: state.subscription.isCancelling,
		isPausing: state.subscription.isPausing,
		isResuming: state.subscription.isResuming,
		isUpgrading: state.subscription.isUpgrading,
		upgradeError: state.subscription.upgradeError,
		isDowngrading: state.subscription.isDowngrading,
		downgradeError: state.subscription.downgradeError,
		isCreatingPortalSession: state.subscription.isCreatingPortalSession,
		cancelError: state.subscription.cancelError,
		pauseError: state.subscription.pauseError,
		resumeError: state.subscription.resumeError,
		portalSessionError: state.subscription.portalSessionError,
		availablePlans: state.subscription.availablePlans || [],
		currentSubscription: state.subscription.currentSubscription,
	}));

	const { showCancellationIncentive } = useUpgradeIncentive();

	// Form state for cancellation feedback
	const [selectedReason, setSelectedReason] = useState(
		options?.selectedReason || '',
	);
	const [additionalComments, setAdditionalComments] = useState(
		options?.additionalComments || '',
	);
	const [isSuccess, setIsSuccess] = useState(false);
	const [isIncentiveShown, setIsIncentiveShown] = useState(
		options?.isIncentiveShown || false,
	);

	// Plan selection state for upgrade/downgrade
	const [selectedPlan, setSelectedPlan] = useState(null);
	const [isYearly, setIsYearly] = useState(false);
	const hasSmsAddon = Boolean(
		currentSubscription?.has_sms_addon ||
		currentSubscription?.subscription?.has_sms_addon ||
		currentSubscription?.usageSummary?.has_sms_addon ||
		currentSubscription?.usageSummary?.hasSmsAddon
	);
	const [includeSmsAddon, setIncludeSmsAddon] = useState(
		options?.withSmsAddon ?? hasSmsAddon ?? false
	);
	const seededSmsAddon = options?.withSmsAddon;

	// Once the user ticks or unticks the card, stop deriving the selection from
	// a subscription that is still being refetched.
	const hasChosenSmsAddon = useRef(false);

	// The modal can mount before the subscription resolves, so seed the card
	// again when the live flag arrives. An explicit caller-supplied value and a
	// choice the user has already made both outrank the seed, so a late resolve
	// can never overwrite either.
	useEffect(() => {
		if (hasChosenSmsAddon.current || seededSmsAddon !== undefined) {
			return;
		}
		setIncludeSmsAddon(hasSmsAddon);
	}, [hasSmsAddon, seededSmsAddon]);

	// The add-on is only offered where its monthly price is billable. Every
	// consumer below reads the gated selection rather than the raw checkbox
	// state, so no yearly path can render, total or submit a monthly-only
	// add-on even if the checkbox was seeded ticked before the interval was
	// known.
	const isSmsAddonAvailable = !isYearly;
	const smsAddonSelected = includeSmsAddon && isSmsAddonAvailable;

	/**
	 * Set the billing interval, dropping an add-on selection the new interval
	 * cannot carry.
	 */
	const chooseInterval = (yearly) => {
		setIsYearly(yearly);
		if (yearly) {
			setIncludeSmsAddon(false);
		}
	};

	/**
	 * Set the SMS add-on selection, ignoring input while the add-on is not
	 * offered on the selected interval.
	 */
	const chooseSmsAddon = (selected) => {
		if (!isSmsAddonAvailable) {
			return;
		}
		hasChosenSmsAddon.current = true;
		setIncludeSmsAddon(selected);
	};

	// The add-on state this screen is asking for, or NO_SMS_ADDON_CHANGE where
	// the modal offered no live choice or the selection already matches what
	// the subscriber holds. Both plan-change paths read this one value.
	const smsAddonChange =
		['upgrade', 'downgrade'].includes(action) &&
		isSmsAddonAvailable &&
		smsAddonSelected !== hasSmsAddon
			? smsAddonSelected
			: NO_SMS_ADDON_CHANGE;

	/**
	 * Apply the add-on selection made on this screen, resolving only once the
	 * request has settled so no caller can close over a pending add-on change.
	 */
	const reconcileSmsAddon = async () => {
		if (smsAddonChange === NO_SMS_ADDON_CHANGE) {
			return;
		}
		try {
			await dispatch(updateSmsAddon(smsAddonChange));
		} catch (smsAddonFailure) {
			// The plan change already went through, so an add-on failure must not
			// be reported as a failed plan change; updateSmsAddon has already
			// surfaced it to the user.
			console.error(
				'SMS add-on update failed after plan change:',
				smsAddonFailure,
			);
		}
	};

	// Fetch available plans if not already loaded
	const [didFetchPlans, setDidFetchPlans] = useState(false);
	if (
		!didFetchPlans &&
		availablePlans.length === 0 &&
		['upgrade', 'downgrade'].includes(action)
	) {
		dispatch(fetchAvailablePlans());
		setDidFetchPlans(true);
	}

	// Cancellation reasons
	const cancellationReasons = [
		'Too Expensive',
		'Lacking key features',
		'Switching to a competitor',
		'Temporary need',
		'Poor Experience',
		'Other',
	];

	const handleReasonChange = (reason) => {
		setSelectedReason(reason);
	};

	const onSubmit = async () => {
		if (!isIncentiveShown && action === 'cancel') {
			setIsIncentiveShown(true);

			showCancellationIncentive({
				onDismiss: () => {
					// Return to the subscription action modal
					setTimeout(() => {
						dispatch(
							openModal(MODAL_TYPES.SUBSCRIPTION_ACTION, {
								...options,
								isIncentiveShown: true,
								selectedReason,
								additionalComments,
							}),
						);
					}, 300);
				},
				onProceedWithCancel: async () => {
					// Skip the incentive and proceed with cancellation
					// setIsIncentiveShown(true); // Mark as shown to skip incentive check
					await onActionPerform(); // Call the existing onSubmit logic
				},

				onConvert: async (planId) => {
					const result = await dispatch(upgradeSubscription(planId));
					if (result?.type?.includes('SUCCESS')) {
						dispatch(
							showToast({
								type: 'success',
								title: 'Success!',
								message: `Successfully upgraded plan!`,
								duration: 4000,
							}),
						);
					}
				},
			});

			return;
		}

		return onActionPerform();
	};

	const onActionPerform = async () => {
		setIsSuccess(false);
		dispatch(clearSubscriptionErrors());

		let actionPromise;
		let targetPlanId = planId; // Use the original planId or selected plan

		// For upgrade/downgrade, use selected plan if available
		if (['upgrade', 'downgrade'].includes(action) && selectedPlan) {
			targetPlanId = SubscriptionHelper.getPlanDatabaseId(
				selectedPlan,
				isYearly,
			);
		}

		switch (action) {
			case 'cancel':
				actionPromise = cancelSubscription(
					true, // immediately = true
					{
						reason: selectedReason || 'No reason provided',
						comments: additionalComments || '',
					},
				);
				// actionPromise = createPortalSession(
				// 	true, // immediately = true
				// 	selectedReason
				// 		? `${selectedReason}. ${additionalComments}`.trim()
				// 		: additionalComments
				// );
				break;

			case 'pause':
				// Use direct pause subscription
				actionPromise = pauseSubscription();
				break;

			case 'resume':
				// Use direct resume subscription
				actionPromise = resumeSubscription();
				break;

			case 'upgrade':
				// For upgrade/downgrade actions, require plan selection
				if (['upgrade', 'downgrade'].includes(action) && !selectedPlan) {
					console.error('Please select a plan for', action);
					return;
				}
				if (!targetPlanId) {
					console.error('Plan ID is required for upgrade');
					return;
				}
				actionPromise = upgradeSubscription(targetPlanId);
				break;

			case 'downgrade':
				// Validation is handled above in upgrade case
				if (!targetPlanId) {
					console.error('Plan ID is required for downgrade');
					return;
				}

				// Check limits before proceeding with downgrade
				if (selectedPlan) {
					// Close current modal and check limits first
					// dispatch(closeModal());
					// The add-on selection travels with the downgrade so it
					// survives the limits check and the confirm modal that replace
					// this screen.
					return dispatch(
						checkDowngradeLimits(
							targetPlanId,
							selectedPlan.name,
							smsAddonChange,
						),
					);
				}

				actionPromise = downgradeSubscription(targetPlanId);
				break;

			case 'portal':
				// actionPromise = createPortalSession();
				break;

			default:
				console.error('Unknown action:', action);
				return;
		}

		if (actionPromise) {
			try {
				const result = await dispatch(actionPromise);

				// The plan change request resolved, so the add-on the user ticked
				// is applied here, keyed on that resolution rather than on the
				// response-type test below that no response body satisfies.
				await reconcileSmsAddon();

				if (result?.type?.includes('SUCCESS')) {
					// Show appropriate success toast message
					const getSuccessMessage = () => {
						switch (action) {
							case 'cancel':
								return 'Your subscription has been cancelled successfully.';
							case 'pause':
								return 'Your subscription has been paused successfully.';
							case 'resume':
								return 'Your subscription has been resumed successfully.';
							case 'upgrade':
								return selectedPlan
									? `Successfully upgraded to ${selectedPlan.name} plan!`
									: 'Your subscription has been upgraded successfully.';
							case 'downgrade':
								return selectedPlan
									? `Successfully downgraded to ${selectedPlan.name} plan!`
									: 'Your subscription has been downgraded successfully.';
							case 'portal':
								return 'Redirecting to billing portal...';
							default:
								return 'Action completed successfully!';
						}
					};

					// Show success toast
					dispatch(
						showToast({
							type: 'success',
							title: 'Success!',
							message: getSuccessMessage(),
							duration: 4000,
						}),
					);

					// Close modal immediately for all actions
					dispatch(closeModal());

					// For portal action, let the thunk handle redirection
					if (['portal'].includes(action)) {
						// The thunk handles redirection to Stripe portal
						return;
					}

					// For subscription changes, refresh the page after a short delay to show updated state
					if (
						['cancel', 'pause', 'resume', 'upgrade', 'downgrade'].includes(
							action,
						)
					) {
						setTimeout(() => {
							window.location.reload();
						}, 1000);
					}
				}
			} catch (error) {
				console.error('Action failed:', error);
				// Show error toast
				dispatch(
					showToast({
						type: 'error',
						title: 'Error',
						message:
							'An error occurred while processing your request. Please try again.',
						duration: 5000,
					}),
				);
			}
		}
	};

	const getModalConfig = () => {
		switch (action) {
			case 'cancel':
				return {
					title: 'Before you go, what made you cancel?',
					confirmText: 'Cancel Membership',
					confirmColor: 'danger',
					// alternativeText: 'Pause Membership Instead',
					// showAlternative: true,
				};

			case 'pause':
				return {
					title: 'Pause Subscription',
					confirmText: 'Pause Subscription',
					confirmColor: 'warning',
				};

			case 'resume':
				return {
					title: 'Resume Subscription',
					confirmText: 'Resume Subscription',
					confirmColor: 'success',
				};

			case 'upgrade':
				return {
					title: selectedPlan
						? `Upgrade to ${selectedPlan.name}`
						: 'Select Plan to Upgrade',
					confirmText: 'Proceed to Checkout',
					confirmColor: 'primary',
				};

			case 'downgrade':
				return {
					title: selectedPlan
						? `Downgrade to ${selectedPlan.name}`
						: 'Select Plan to Downgrade',
					confirmText: 'Proceed to Checkout',
					confirmColor: 'primary',
				};

			case 'portal':
				return {
					title: 'Manage Billing',
					confirmText: 'Open Billing Portal',
					confirmColor: 'primary',
				};

			default:
				return {
					title: 'Subscription Action',
					confirmText: 'Confirm',
					confirmColor: 'primary',
				};
		}
	};

	const renderCancellationForm = () => (
		<div>
			<div className="mb-4">
				{cancellationReasons.map((reason) => (
					<div key={reason} className="form-check mb-2">
						<input
							className="form-check-input"
							type="radio"
							name="cancellationReason"
							id={`reason-${reason}`}
							value={reason}
							checked={selectedReason === reason}
							onChange={() => handleReasonChange(reason)}
						/>
						<label className="form-check-label" htmlFor={`reason-${reason}`}>
							{reason}
						</label>
					</div>
				))}
			</div>

			<div className="mb-3">
				<label className="form-label text-muted">Tell us more (optional)</label>
				<textarea
					className="form-control"
					rows="4"
					placeholder="Share your thoughts..."
					value={additionalComments}
					onChange={(e) => setAdditionalComments(e.target.value)}
				/>
			</div>
		</div>
	);

	// Get available plans for upgrade/downgrade selection
	const combinedPlans = useMemo(() => {
		if (
			!['upgrade', 'downgrade'].includes(action) ||
			availablePlans.length === 0
		) {
			return [];
		}
		const allPlans =
			SubscriptionHelper.combineSubscriptionPlans(availablePlans);
		// Filter out free and enterprise plans completely from upgrade/downgrade options
		return allPlans.filter(
			(plan) =>
				plan.id !== SUBSCRIPTION_PLANS.FREE &&
				plan.id !== SUBSCRIPTION_PLANS.ENTERPRISE,
		);
	}, [action, availablePlans]);

	// Get current subscription plan details
	const currentPlan = useMemo(() => {
		if (!currentSubscription) return null;
		return SubscriptionHelper.getCurrentSubscriptionPlan(
			currentSubscription,
			availablePlans,
		);
	}, [currentSubscription, availablePlans]);

	// Filter plans based on action (upgrade/downgrade)
	const canDisablePlans = useMemo(() => {
		if (!['upgrade', 'downgrade'].includes(action) || !currentPlan) {
			return combinedPlans;
		}

		const currentPlanType = SubscriptionHelper.extractPlanType(
			currentPlan.name,
		);
		const currentPlanLevel =
			SubscriptionHelper.PLAN_HIERARCHY[currentPlanType] || 0;

		const filtered = combinedPlans.map((plan) => {
			const planType = SubscriptionHelper.extractPlanType(plan.name);
			const planLevel = SubscriptionHelper.PLAN_HIERARCHY[planType] || 0;

			let disabled = false;
			if (action === 'upgrade') {
				disabled = planLevel <= currentPlanLevel;
			} else if (action === 'downgrade') {
				// For downgrade: only show plans with lower tier level
				disabled = planLevel >= currentPlanLevel;
			}
			return {
				...plan,
				disabled,
			};
		});
		// .filter((plan) => {
		// 	const planLevel = SubscriptionHelper.PLAN_HIERARCHY[plan.id] || 0;
		// 	if (action === 'upgrade') {
		// 		// For upgrade: only show plans with higher tier level
		// 		const shouldShow = planLevel > currentPlanLevel;
		// 		return shouldShow;
		// 	} else if (action === 'downgrade') {
		// 		// For downgrade: only show plans with lower tier level
		// 		const shouldShow = planLevel < currentPlanLevel;
		// 		return shouldShow;
		// 	}

		// 	return false;
		// })
		return filtered;
	}, [action, combinedPlans, currentPlan]);

	const filteredPlans = useMemo(() => {
		return canDisablePlans.filter((plan) => !plan.disabled);
	}, [canDisablePlans]);

	const renderPlanSelectionForm = () => {
		if (filteredPlans.length === 0 && false) {
			return (
				<div className="alert alert-info">
					<p className="mb-0">
						{action === 'upgrade'
							? 'No higher-tier plans available for upgrade.'
							: 'No lower-tier plans available for downgrade.'}
					</p>
				</div>
			);
		}

		return (
			<div>
				{/* Billing toggle */}
				<div className="d-flex justify-content-center mb-4">
					<div className="btn-group" role="group">
						<button
							type="button"
							className={`btn ${
								!isYearly ? 'btn-primary' : 'btn-outline-secondary'
							}`}
							onClick={() => chooseInterval(false)}
						>
							Monthly
						</button>
						<button
							type="button"
							className={`btn ${
								isYearly ? 'btn-primary' : 'btn-outline-secondary'
							}`}
							onClick={() => chooseInterval(true)}
						>
							Yearly
						</button>
					</div>
				</div>

				{/* Plan selection */}
				<div className="row g-3">
					{canDisablePlans.map((plan) => {
						const price = SubscriptionHelper.getPlanPrice(plan, isYearly);
						const isSelected = selectedPlan?.id === plan.id;
						const isDisabled =
							!SubscriptionHelper.getPlanDatabaseId(plan, isYearly) ||
							plan.disabled;

						return (
							<div key={plan.id} className="col-12">
								<div
									className={`card plan-selection-card bg-light ${
										isSelected ? 'border-primary' : ''
									} ${isDisabled ? 'opacity-50' : 'cursor-pointer'}`}
									onClick={() => !isDisabled && setSelectedPlan(plan)}
									style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
								>
									<div className="card-body p-3">
										<div className="d-flex justify-content-between align-items-center">
											<div>
												<h6 className="card-title mb-1">
													{plan.name}
													{plan.popular && (
														<span className="badge bg-success ms-2">
															Popular
														</span>
													)}
												</h6>
												<p className="text-muted small mb-0">{plan.subtitle}</p>
											</div>
											<div className="text-end">
												<div className="h5 mb-0">
													${price}
													<small className="text-muted">/mo</small>
												</div>
												{isYearly && (
													<small className="text-success">Billed yearly</small>
												)}
											</div>
										</div>
										{isDisabled && (
											<div className="mt-2">
												<small className="text-muted">
													Not available for {isYearly ? 'yearly' : 'monthly'}{' '}
													billing
												</small>
											</div>
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>

				{filteredPlans.length === 0 && (
					<div className="alert alert-info mt-3">
						<p className="mb-0">
							{action === 'upgrade'
								? 'No higher-tier plans available for upgrade.'
								: 'No lower-tier plans available for downgrade.'}
						</p>
					</div>
				)}

				{/* SMS Add-on Selection - monthly billing only */}
				<div className="mt-3">
					<div
						className={`card p-3 ${
							isSmsAddonAvailable ? 'cursor-pointer' : 'opacity-50'
						} ${
							smsAddonSelected
								? 'border-success bg-white shadow-sm'
								: 'border-light bg-light'
						}`}
						style={{
							cursor: isSmsAddonAvailable ? 'pointer' : 'not-allowed',
							transition: 'all 0.2s ease',
							borderWidth: smsAddonSelected ? '2px' : '1px',
						}}
						onClick={() => chooseSmsAddon(!smsAddonSelected)}
					>
						<div className="d-flex justify-content-between align-items-center">
							<div className="d-flex align-items-center pr-2">
								<div className="mr-3" style={{ fontSize: '1.4rem', lineHeight: 1 }}>
									📱
								</div>
								<div>
									<div className="d-flex align-items-center">
										<span
											className="font-weight-bold"
											style={{ fontSize: '0.92rem', color: '#111827' }}
										>
											SMS Instant Alerts
										</span>
										<span
											className="badge bg-success ms-2 text-white"
											style={{ fontSize: '0.75rem' }}
										>
											+${SMS_ADDON_MONTHLY_PRICE.toFixed(2)}/mo
										</span>
									</div>
									<small className="text-muted d-block mt-1">
										Instant real-time SMS for Stop Work Orders, ECB/DOB hearings
										and critical deadlines
									</small>
								</div>
							</div>
							<div className="form-check m-0">
								<input
									type="checkbox"
									className="form-check-input"
									checked={smsAddonSelected}
									disabled={!isSmsAddonAvailable}
									onChange={(e) => chooseSmsAddon(e.target.checked)}
									style={{
										cursor: isSmsAddonAvailable ? 'pointer' : 'not-allowed',
										width: '1.2rem',
										height: '1.2rem',
									}}
								/>
							</div>
						</div>
						{!isSmsAddonAvailable && (
							<div className="mt-2">
								<small className="text-muted d-block">
									{SMS_ADDON_MONTHLY_ONLY_NOTE}
								</small>
							</div>
						)}
					</div>
				</div>

				{selectedPlan && (
					<div className="mt-3">
						<div className="alert alert-info mb-0">
							<div className="d-flex justify-content-between align-items-center">
								<div>
									<strong>Selected Plan:</strong> {selectedPlan.name}
									{smsAddonSelected && (
										<span className="text-success ms-1">+ SMS Add-on</span>
									)}
								</div>
								<div className="text-end">
									<strong>
										$
										{(
											parseFloat(
												SubscriptionHelper.getPlanPrice(
													selectedPlan,
													isYearly,
												),
											) + (smsAddonSelected ? SMS_ADDON_MONTHLY_PRICE : 0)
										).toFixed(2)}
										/mo
									</strong>
									{isYearly ? (
										<small className="d-block text-success">
											Billed yearly
											{smsAddonSelected
												? ` + $${SMS_ADDON_MONTHLY_PRICE.toFixed(2)}/mo SMS`
												: ''}
										</small>
									) : (
										<small className="d-block text-muted">Billed monthly</small>
									)}
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderActionContent = () => {
		switch (action) {
			case 'cancel':
				return renderCancellationForm();

			case 'pause':
				return (
					<div>
						<p className="mb-3">
							Your subscription will be paused immediately. You won't be charged
							for the next billing cycle and can resume anytime from your
							subscription settings.
						</p>
					</div>
				);

			case 'resume':
				return (
					<div>
						<p className="mb-3">
							Your subscription will be resumed immediately. Billing will
							restart according to your plan's billing cycle.
						</p>
					</div>
				);

			case 'upgrade':
			case 'downgrade':
				return renderPlanSelectionForm();

			case 'portal':
				return (
					<div>
						<p className="mb-3">
							You'll be redirected to the Stripe Customer Portal where you can
							manage your billing information, download invoices, and update
							payment methods.
						</p>
					</div>
				);

			default:
				return (
					<div>
						<p>Are you sure you want to proceed with this action?</p>
					</div>
				);
		}
	};

	const handleAlternativeAction = async () => {
		if (action === 'cancel') {
			// Switch to pause action
			setIsSuccess(false);
			dispatch(clearSubscriptionErrors());
			try {
				await dispatch(pauseSubscription());
			} catch (error) {
				console.error('Pause action failed:', error);
				// Show error toast
				dispatch(
					showToast({
						type: 'error',
						title: 'Error',
						message: 'Failed to pause subscription. Please try again.',
						duration: 5000,
					}),
				);
			}
		}
	};

	const getCurrentError = () => {
		switch (action) {
			case 'cancel':
				return cancelError?.message || cancelError;
			case 'pause':
				return pauseError?.message || pauseError;
			case 'resume':
				return resumeError?.message || resumeError;
			case 'upgrade':
				return upgradeError?.message || upgradeError;
			case 'downgrade':
				return downgradeError?.message || downgradeError;
			case 'portal':
				return portalSessionError?.message || portalSessionError;
			default:
				return null;
		}
	};

	const isLoading = () => {
		switch (action) {
			case 'cancel':
				return isCancelling;
			case 'pause':
				return isPausing;
			case 'resume':
				return isResuming;
			case 'upgrade':
				return isUpgrading;
			case 'downgrade':
				return isDowngrading;
			case 'portal':
				return isCreatingPortalSession;
			default:
				return isPerformingAction;
		}
	};

	const config = getModalConfig();
	const currentError = getCurrentError();
	const loading = isLoading();

	const successAlert = (
		<Alert color="success">
			<div>
				{action === 'cancel' && 'Your subscription has been cancelled.'}
				{action === 'pause' && 'Your subscription has been paused.'}
				{action === 'resume' && 'Your subscription has been resumed.'}
				{action === 'upgrade' && 'Your subscription has been upgraded.'}
				{action === 'downgrade' && 'Your subscription has been downgraded.'}
				{['portal'].includes(action) && 'Redirecting...'}
			</div>
		</Alert>
	);

	const errorAlert = currentError && (
		<Alert color="danger">
			<div>{currentError}</div>
		</Alert>
	);

	return (
		<StandardModal
			title={config.title}
			size="md"
			closeOnOutsideClick
			confirmText={config.confirmText}
			confirmColor={config.confirmColor}
			closeLabel={action === 'cancel' ? 'Back to Dashboard' : 'Cancel'}
			onConfirm={onSubmit}
			disableConfirm={
				loading || (['upgrade', 'downgrade'].includes(action) && !selectedPlan)
			}
			disableClose={loading}
			footerButtons={
				config.showAlternative && (
					<button
						type="button"
						className="btn btn-outline-secondary me-2"
						onClick={handleAlternativeAction}
						disabled={loading}
					>
						{config.alternativeText}
					</button>
				)
			}
		>
			<div>
				{isSuccess && successAlert}
				{errorAlert}
				{loading ? <Spinner /> : <div>{renderActionContent()}</div>}
			</div>
		</StandardModal>
	);
};

export default SubscriptionActionModal;
